import { FunctionComponent, memo, useEffect, useMemo, useState } from 'react';
import { DataBadge } from './components/DataBadge';
import { Box, Grid2, Stack, SvgIcon, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import type { Theme } from '@mui/material/styles';
import { Environment } from '../../environment';
import { useLazyGetAggregatedTicketsQuery } from '../../liftoperator';
import {
    AggregatedTicket,
    AggregatedTicketSearchParams,
    getSumMoney,
} from '../../aggregatedTicket';
import { getMoneyAsString, addUpCosts } from '../../money';
import {
    ArrowTrendingUpIcon,
    ArrowUpOnSquareStackIcon,
    XMarkIcon,
} from '@heroicons/react/24/solid';
import XCircleIcon from '@heroicons/react/24/solid/XMarkIcon';
import { PieDiagram } from './components/PieDiagram';
import ArrowDownOnSquareIcon from '@heroicons/react/24/solid/ArrowDownOnSquareIcon';
import { LoadingButton } from '@mui/lab';
import difference from 'lodash/difference';
import { useTranslation } from 'react-i18next';
import { TicketSalesStatistics } from './TicketSalesStatistics';
import {
    StatisticsFilter,
    getPresetRange,
    type B2BUser,
} from './StatisticsFilter';
import { useExportAggregatedTickets } from '../lib/useExportAggregatedTickets';
import { usePagination } from '@starjack/shared-components';
import { StarjackTable } from '@starjack/shared-components';

const getSales = (
    aggregatedTickets: AggregatedTicket[],
    t: (key: string) => string,
    propName: 'name' | 'liftOperatorTicketType' | 'personName'
): { labels: string[]; values: number[]; counts: number[] } => {
    const tickets = aggregatedTickets || [];

    const aggregated = tickets.reduce<
        Record<string, { amount: number; count: number }>
    >((acc, ticket) => {
        const field = ticket[propName];
        const fieldValue = Array.isArray(field) ? field[0] : field;

        if (fieldValue === undefined) return acc;

        if (!acc[fieldValue]) {
            acc[fieldValue] = { amount: 0, count: 0 };
        }
        acc[fieldValue].amount += ticket.sumSales.amount;
        acc[fieldValue].count += ticket.totalSales;

        return acc;
    }, {});

    const sorted = Object.entries(aggregated).sort(
        (a, b) => b[1].amount - a[1].amount
    );

    const result: { labels: string[]; values: number[]; counts: number[] } = {
        labels: [],
        values: [],
        counts: [],
    };

    const itemsToShow = sorted.length <= 4 ? sorted : sorted.slice(0, 4);
    const restItems = sorted.length > 4 ? sorted.slice(4) : [];

    for (const [label, data] of itemsToShow) {
        if (propName === 'liftOperatorTicketType') {
            result.labels.push(
                label === 'SEASON' ? t('seasonTicket') : t('dayTicket')
            );
        } else {
            result.labels.push(label);
        }
        result.values.push(data.amount);
        result.counts.push(data.count);
    }

    if (restItems.length > 0) {
        const restAmount = restItems.reduce(
            (sum, [, data]) => sum + data.amount,
            0
        );
        const restCount = restItems.reduce(
            (sum, [, data]) => sum + data.count,
            0
        );
        result.labels.push('Sonstige');
        result.values.push(restAmount);
        result.counts.push(restCount);
    }

    return result;
};

function getTicketTypeString(
    liftOperatorTicketType: string,
    t: (key: string) => string,
    duration?: number
): string {
    if (liftOperatorTicketType === 'SEASON') {
        return t('seasonTicket');
    }
    return `${duration ? `${duration} ` : ''}${t('dayTicket')}`;
}

type StatisticsContentProps = {
    environment: Environment[];
    b2bUsers?: B2BUser[];
};

export const StatisticsContent: FunctionComponent<StatisticsContentProps> = ({
    environment,
    b2bUsers,
}) => {
    const theme = useTheme() as Theme & { page?: { verticalSpacing?: number } };
    const [
        getAggregatedTicket,
        { data: aggregatedTickets = [], isUninitialized, isFetching, error },
    ] = useLazyGetAggregatedTicketsQuery();
    const { t } = useTranslation();

    const [exportTickets, { isLoading: exportLoading }] =
        useExportAggregatedTickets();
    const [showLoadingState, setShowLoadingState] = useState(true);
    const [ticketSearch, setTicketSearch] =
        useState<AggregatedTicketSearchParams>({
            environments: environment,
        });
    const { currentItems, setNextPage, setItemsPerPage, itemsPerPage } =
        usePagination<AggregatedTicket>({
            items: aggregatedTickets,
            itemsPerPage: 10,
        });

    if (difference(ticketSearch.environments ?? [], environment).length) {
        setTicketSearch({
            ...ticketSearch,
            environments: environment,
        });
    }

    const sums = useMemo(
        () => ({
            sales: getMoneyAsString(
                addUpCosts(getSumMoney(aggregatedTickets, 'sumSales'))
            ),
            cancellations: getMoneyAsString(
                addUpCosts(getSumMoney(aggregatedTickets, 'sumCancellations'))
            ),
            deposits: getMoneyAsString(
                addUpCosts(getSumMoney(aggregatedTickets, 'sumDepositAmounts'))
            ),
            cancelledDeposits: getMoneyAsString(
                addUpCosts(
                    getSumMoney(aggregatedTickets, 'sumCancelledDepositAmounts')
                )
            ),
        }),
        [aggregatedTickets]
    );

    useEffect(() => {
        const loading = isFetching || isUninitialized;

        if (loading) {
            setShowLoadingState(loading);
        } else {
            setTimeout(() => {
                setShowLoadingState(false);
            }, 800);
        }
    }, [isFetching, isUninitialized]);

    useEffect(() => {
        const [orderDateFrom, orderDateTo] = getPresetRange('last30days');
        getAggregatedTicket({
            environments: environment,
            orderDateFrom,
            orderDateTo,
        });
    }, [environment]);

    const ticketSales = useMemo(
        () => getSales(aggregatedTickets, t, 'name'),
        [aggregatedTickets, t]
    );
    const personNameSales = useMemo(
        () => getSales(aggregatedTickets, t, 'personName'),
        [aggregatedTickets, t]
    );
    const ticketType = useMemo(
        () => getSales(aggregatedTickets, t, 'liftOperatorTicketType'),
        [aggregatedTickets, t]
    );

    if (isUninitialized) return null;

    const verticalSpacing = theme.page?.verticalSpacing ?? 3;

    return (
        <>
            <TicketSalesStatistics environments={environment} />

            <Stack
                sx={{
                    alignItems: 'flex-start',
                }}
                spacing={{ xs: 2, md: verticalSpacing }}
            >
                <StatisticsFilter
                    loading={showLoadingState}
                    b2bUsers={b2bUsers}
                    environments={environment}
                    onSearch={(params) => {
                        setTicketSearch({
                            environments: environment,
                            ...params,
                        });
                        getAggregatedTicket({
                            environments: environment,
                            ...params,
                        });
                    }}
                />

                <LoadingButton
                    sx={{
                        mr: { xs: 0, md: 'auto' },
                        mb: 4,
                        width: { xs: '100%', md: 'auto' },
                    }}
                    loading={exportLoading}
                    onClick={() => {
                        exportTickets(ticketSearch);
                    }}
                    color='inherit'
                    startIcon={
                        <SvgIcon fontSize='small'>
                            <ArrowDownOnSquareIcon />
                        </SvgIcon>
                    }
                >
                    {t('exportDetailedStatistics')}
                </LoadingButton>

                <Grid2
                    container
                    spacing={3}
                    sx={{
                        width: '100%',
                    }}
                >
                    <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
                        <DataBadge
                            title={t('salesInEur')}
                            avatarColor={String(theme.palette.primary.main)}
                            avatarIcon={<ArrowTrendingUpIcon />}
                            data={{
                                value: String(sums.sales),
                            }}
                            loading={showLoadingState}
                        />
                    </Grid2>

                    <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
                        <DataBadge
                            title={t('cancellationsInEur')}
                            avatarColor={String(theme.palette.error.main)}
                            avatarIcon={<XMarkIcon />}
                            data={{
                                value: String(sums.cancellations),
                            }}
                            loading={showLoadingState}
                        />
                    </Grid2>

                    <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
                        <DataBadge
                            title={t('depositInEur')}
                            avatarColor={String(theme.palette.success.main)}
                            avatarIcon={<ArrowUpOnSquareStackIcon />}
                            data={{
                                value: String(sums.deposits),
                            }}
                            loading={showLoadingState}
                        />
                    </Grid2>

                    <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
                        <DataBadge
                            title={t('depositCancelledInEur')}
                            avatarColor={String(theme.palette.secondary.main)}
                            avatarIcon={<XCircleIcon />}
                            data={{
                                value: String(sums.cancelledDeposits),
                            }}
                            loading={showLoadingState}
                        />
                    </Grid2>
                </Grid2>

                <Grid2
                    container
                    spacing={3}
                    sx={{
                        width: '100%',
                    }}
                >
                    <Grid2 size={{ xs: 12, md: 4 }}>
                        <PieDiagram
                            labels={ticketSales.labels}
                            chartSeries={ticketSales.values}
                            counts={ticketSales.counts}
                            title={t('salesByTicketName')}
                        />
                    </Grid2>

                    <Grid2 size={{ xs: 12, md: 4 }}>
                        <PieDiagram
                            labels={ticketType.labels}
                            chartSeries={ticketType.values}
                            counts={ticketType.counts}
                            title={t('salesByTicketType')}
                        />
                    </Grid2>

                    <Grid2 size={{ xs: 12, md: 4 }}>
                        <PieDiagram
                            labels={personNameSales.labels}
                            chartSeries={personNameSales.values}
                            counts={personNameSales.counts}
                            title={t('salesByPersonName')}
                        />
                    </Grid2>
                </Grid2>

                <StarjackTable<AggregatedTicket>
                    count={aggregatedTickets.length}
                    items={currentItems}
                    columns={[
                        {
                            label: t('ticketName'),
                            itemTransform: (ticket) => (
                                <TicketName ticket={ticket} />
                            ),
                        },
                        {
                            label: t('ticketType'),
                            itemTransform: (ticket) =>
                                getTicketTypeString(
                                    ticket.liftOperatorTicketType,
                                    t,
                                    ticket.duration
                                ),
                        },
                        {
                            label: t('personType'),
                            itemKey: 'personName',
                        },

                        {
                            label: t('sales'),
                            itemKey: 'totalSales',
                            options: {
                                headerCellProps: {
                                    align: 'right',
                                },
                                rowCellProps: {
                                    align: 'right',
                                },
                            },
                        },

                        {
                            label: t('priceSale'),
                            itemTransform: (ticket) =>
                                getMoneyAsString(ticket.sumSales),
                            options: {
                                headerCellProps: {
                                    align: 'right',
                                },
                                rowCellProps: {
                                    align: 'right',
                                },
                            },
                        },

                        {
                            label: t('priceCancellation'),
                            itemTransform: (ticket) =>
                                getMoneyAsString(ticket.sumCancellations),
                            options: {
                                headerCellProps: {
                                    align: 'right',
                                },
                                rowCellProps: {
                                    align: 'right',
                                },
                            },
                        },
                    ]}
                    rowsPerPage={itemsPerPage}
                    onRowsPerPageChange={setItemsPerPage}
                    onPageChange={(nextValue) => setNextPage(nextValue)}
                    loading={showLoadingState}
                    error={error}
                />
            </Stack>
        </>
    );
};

const TicketName: FunctionComponent<{ ticket: AggregatedTicket }> = memo(
    ({ ticket }) => (
        <Box
            sx={{
                display: 'flex',
                justifyContent: 'center',
                flexDirection: 'column',
            }}
        >
            <Typography
                sx={{
                    fontSize: '0.875rem',
                    lineHeight: '1.43',
                    letterSpacing: '0.01071em',
                }}
                gutterBottom
            >
                {ticket.name}
            </Typography>

            {!!ticket.conditions.length && (
                <Typography
                    sx={{
                        lineHeight: '1.43',
                        letterSpacing: '0.01071em',
                        ml: '4px !important',
                        fontSize: '0.85rem !important',
                    }}
                >
                    {ticket.conditions.map(
                        (c, index) =>
                            `${c}${
                                index < ticket.conditions.length - 1 ? ', ' : ''
                            }`
                    )}
                </Typography>
            )}
        </Box>
    )
);

TicketName.displayName = 'TicketName';
